import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Kbd,
  LinkBox,
  LinkOverlay,
  Skeleton,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiMessageSquare, FiSearch } from "react-icons/fi";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useListContractReportsQuery, type ContractReportDto } from "../../autogen/bff-api";
import { AssistantPanelContext } from "../../common/agent/assistantPanelContext";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { useSub } from "../../common/subscription/useSub";
import { base64Encode } from "../../common/utils/base64";
import { urls } from "../../urls";

export const SHOWN_HITS = 8;
const MIN_TERM_LENGTH = 2;
const TYPING_PAUSE_MS = 250;

export const HomeSearchBar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canViewContracts, canUseContractSearch } = useSub();
  const askAssistant = useContext(AssistantPanelContext)?.openWithQuestion;
  const askContractSearch = canUseContractSearch
    ? (question: string) => navigate(`${urls.app.contracts.search}?q=${encodeURIComponent(question)}`)
    : undefined;
  const askQuestion = askAssistant ?? askContractSearch;
  const askLabel = askAssistant ? t("Ask the assistant") : t("Ask the contract search");
  const inputRef = useRef<HTMLInputElement>(null);
  const [term, setTerm] = useState("");
  const searched = useSettledTerm(term.trim());

  useEffect(() => {
    const focusOnShortcut = (event: KeyboardEvent) => {
      if (!isSearchShortcut(event)) return;
      event.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener("keydown", focusOnShortcut);
    return () => window.removeEventListener("keydown", focusOnShortcut);
  }, []);

  if (!canViewContracts) return null;

  const ask = () => {
    const question = term.trim();
    if (question && askQuestion) askQuestion(question);
  };

  return (
    <Box mb="4">
      <InputGroup size="lg">
        <InputLeftElement pointerEvents="none" h="full">
          <FiSearch color="var(--chakra-colors-smMuted)" />
        </InputLeftElement>
        <Input
          ref={inputRef}
          value={term}
          bg="smBackground"
          borderColor="smInputBorder"
          rounded="xl"
          fontSize="md"
          aria-label={t("Search contracts") ?? undefined}
          placeholder={t("Search contracts by title, counterparty, project or reference number…") ?? undefined}
          _placeholder={{ color: "smMuted" }}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") ask();
          }}
        />
        <InputRightElement h="full" pointerEvents="none">
          <Kbd color="smMuted" borderColor="smBorder">
            {searchShortcutLabel()}
          </Kbd>
        </InputRightElement>
      </InputGroup>
      {searched.length >= MIN_TERM_LENGTH && (
        <SearchHits term={searched} askLabel={askLabel} onAsk={askQuestion && term.trim() ? ask : undefined} />
      )}
    </Box>
  );
};

const SearchHits = ({ term, askLabel, onAsk }: { term: string; askLabel: string; onAsk?: () => void }) => {
  const { t } = useTranslation();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const q = base64Encode(
    JSON.stringify({ owningOrganizationId: selectedOrg.id, quickSearchTerm: term, limit: SHOWN_HITS, skip: 0 }),
  );
  const { currentData, isFetching, isError, refetch } = useListContractReportsQuery({ q });
  const hits = currentData?.reports ?? [];

  return (
    <Box
      mt="2"
      p="3"
      bg="smBackground"
      border="1px solid"
      borderColor="smBorder"
      rounded="xl"
      role="region"
      aria-label={t("Contracts that match") ?? undefined}
      aria-busy={isFetching}
    >
      {isError ? (
        <Flex align="center" justify="space-between" gap="3">
          <Text fontSize="sm" color="smError">
            {t("The contracts could not be searched.")}
          </Text>
          <Button size="sm" variant="ghost" onClick={() => void refetch()}>
            {t("Try again")}
          </Button>
        </Flex>
      ) : currentData == null || isFetching ? (
        <Stack spacing="2">
          <Skeleton h="5" />
          <Skeleton h="5" />
        </Stack>
      ) : hits.length === 0 ? (
        <Text fontSize="sm" color="smMuted">
          {t("No contracts match «{{term}}».", { term })}
        </Text>
      ) : (
        <Stack spacing="1">
          {hits.map((hit) => (
            <ContractHit key={hit.id} hit={hit} />
          ))}
          {currentData.count > hits.length && (
            <Text fontSize="xs" color="smMuted" pt="1">
              {t("Showing {{shown}} of {{count}} contracts. Type more to narrow the search.", {
                shown: hits.length,
                count: currentData.count,
              })}
            </Text>
          )}
        </Stack>
      )}
      {onAsk && (
        <Button mt="3" size="sm" variant="outline" colorScheme="teal" leftIcon={<FiMessageSquare />} onClick={onAsk}>
          {askLabel}
        </Button>
      )}
    </Box>
  );
};

const ContractHit = ({ hit }: { hit: ContractReportDto }) => (
  <LinkBox px="2" py="1.5" rounded="md" _hover={{ bg: "smBackgroundSecondary" }}>
    <LinkOverlay as={RouterLink} to={urls.app.contracts.view.go(hit.id)}>
      <Text fontSize="sm" fontWeight="medium" color="smText" noOfLines={1}>
        {hit.title}
      </Text>
    </LinkOverlay>
    {hit.counterpartyName && (
      <Text fontSize="xs" color="smMuted" noOfLines={1}>
        {hit.counterpartyName}
      </Text>
    )}
  </LinkBox>
);

const useSettledTerm = (term: string): string => {
  const [settled, setSettled] = useState(term);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(term), TYPING_PAUSE_MS);
    return () => window.clearTimeout(timer);
  }, [term]);
  return settled;
};

const usesCommandKey = (): boolean => /Mac|iPhone|iPad/.test(navigator.platform);

export const isSearchShortcut = (event: KeyboardEvent): boolean =>
  event.key.toLowerCase() === "k" &&
  !event.altKey &&
  !event.shiftKey &&
  (usesCommandKey() ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey);

export const searchShortcutLabel = (): string => (usesCommandKey() ? "⌘K" : "Ctrl+K");
